import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  Grid,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  CircularProgress,
  Button,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Checkbox,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import BusinessIcon from "@mui/icons-material/Business";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { googleMapsAPI, leadGenAPI, linkedinAPI } from "../services/api";
import toast from "react-hot-toast";

// TabPanel component
const TabPanel = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
};

const LeadHistory = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0); // 0 = Google Maps, 1 = LinkedIn
  const [batches, setBatches] = useState([]);
  const [filteredBatches, setFilteredBatches] = useState([]);
  const [linkedinJobs, setLinkedinJobs] = useState([]);
  const [filteredLinkedinJobs, setFilteredLinkedinJobs] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Batch details view states
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState([]); // Array of company IDs
  const [decisionMakers, setDecisionMakers] = useState([]);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
  const [isLoadingDecisionMakers, setIsLoadingDecisionMakers] = useState(false);
  const [selectedDecisionMaker, setSelectedDecisionMaker] = useState(null);
  const [profileModalOpen, setProfileModalOpen] = useState(false);

  // Hardcoded roles for decision makers
  const availableRoles = [
    "owner",
    "founder",
    "c_suite",
    "partner",
    "vp",
    "head",
    "director",
    "manager",
    "senior",
    "entry",
    "intern",
  ];
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [noOfDecisionMakers, setNoOfDecisionMakers] = useState(1);

  // Fetch batches from API
  useEffect(() => {
    if (activeTab === 0 && !selectedBatch) {
      // Only fetch for Google Maps tab and when not viewing batch details
      fetchBatches();
    } else if (activeTab === 1 && !selectedBatch) {
      // Fetch LinkedIn jobs when LinkedIn tab is active
      fetchLinkedinJobs();
    }
  }, [activeTab, selectedBatch]);

  // Filter batches based on search query
  useEffect(() => {
    if (activeTab === 0) {
      // Filter Google Maps batches
      if (searchQuery.trim() === "") {
        setFilteredBatches(batches);
      } else {
        const filtered = batches.filter((batch) =>
          batch.search_name.toLowerCase().includes(searchQuery.toLowerCase())
        );
        setFilteredBatches(filtered);
      }
    } else {
      // Filter LinkedIn jobs
      if (searchQuery.trim() === "") {
        setFilteredLinkedinJobs(linkedinJobs);
      } else {
        const filtered = linkedinJobs.filter((job) =>
          (job.search_name || "")
            .toLowerCase()
            .includes(searchQuery.toLowerCase())
        );
        setFilteredLinkedinJobs(filtered);
      }
    }
  }, [searchQuery, batches, linkedinJobs, activeTab]);

  const fetchBatches = async () => {
    setIsLoading(true);
    try {
      const response = await googleMapsAPI.getJobs();
      if (response && response.jobs) {
        setBatches(response.jobs);
        setFilteredBatches(response.jobs);
      } else {
        setBatches([]);
        setFilteredBatches([]);
      }
    } catch (error) {
      console.error("Error fetching batches:", error);
      toast.error("Failed to load batches");
      setBatches([]);
      setFilteredBatches([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchLinkedinJobs = async () => {
    setIsLoading(true);
    try {
      const response = await linkedinAPI.getJobs();
      if (response && response.jobs) {
        setLinkedinJobs(response.jobs);
        setFilteredLinkedinJobs(response.jobs);
      } else {
        setLinkedinJobs([]);
        setFilteredLinkedinJobs([]);
      }
    } catch (error) {
      console.error("Error fetching LinkedIn jobs:", error);
      toast.error("Failed to load LinkedIn jobs");
      setLinkedinJobs([]);
      setFilteredLinkedinJobs([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBatchClick = async (batch) => {
    const source = activeTab === 0 ? "googlemaps" : "linkedin";

    // For both Google Maps and LinkedIn, navigate to batch history details page
    const searchName = batch.search_name || batch.keyword_name || "";
    const params = new URLSearchParams({
      job_id: batch.job_id,
      source: source,
    });

    // Add search_name for LinkedIn
    if (source === "linkedin" && searchName) {
      params.append("search_name", searchName);
    }

    navigate(`/batch-history-details?${params.toString()}`);
  };

  const handleCompanyToggle = (company) => {
    // Use a unique identifier - prefer place_id, then company_apollo_id, then id
    const companyId =
      company.place_id ||
      company.company_apollo_id ||
      company.id ||
      `company_${companies.indexOf(company)}`;

    setSelectedCompanies((prev) => {
      if (prev.includes(companyId)) {
        return prev.filter((id) => id !== companyId);
      } else {
        return [...prev, companyId];
      }
    });
    // Reset decision makers when selection changes
    setDecisionMakers([]);
  };

  const handleRoleToggle = (role) => {
    setSelectedRoles((prev) => {
      if (prev.includes(role)) {
        return prev.filter((r) => r !== role);
      } else {
        return [...prev, role];
      }
    });
  };

  const handleFindDecisionMakers = async () => {
    if (selectedCompanies.length === 0) {
      toast.error("Please select at least one company");
      return;
    }
    if (selectedRoles.length === 0) {
      toast.error("Please select at least one role");
      return;
    }
    if (
      !noOfDecisionMakers ||
      noOfDecisionMakers < 1 ||
      noOfDecisionMakers > 20
    ) {
      toast.error("Please enter a valid number of decision makers (1-20)");
      return;
    }

    setIsLoadingDecisionMakers(true);
    try {
      // Get selected company objects from companies array
      const selectedCompanyObjects = companies.filter((company) => {
        const companyId =
          company.place_id ||
          company.company_apollo_id ||
          company.id ||
          `company_${companies.indexOf(company)}`;
        return selectedCompanies.includes(companyId);
      });

      // Extract company names
      const companiesNameList = selectedCompanyObjects.map(
        (company) => company.name || "Unknown Company"
      );

      // Extract website URLs from selected companies
      const websiteUrls = selectedCompanyObjects
        .map((company) => {
          const url = company.website || company.website_url;
          // Only include valid URLs
          if (
            url &&
            (url.startsWith("http://") || url.startsWith("https://"))
          ) {
            return url;
          } else if (url) {
            // Add https:// if URL doesn't start with http
            return `https://${url}`;
          }
          return null;
        })
        .filter((url) => url !== null); // Remove null/undefined values

      // Prepare Apollo people search payload
      const apolloPayload = {
        google_maps_agent_call: true, // Since this is Google Maps tab
        no_of_decision_makers: parseInt(noOfDecisionMakers),
        companies_name_list: companiesNameList,
        list_of_roles: selectedRoles,
        website_urls: websiteUrls,
      };

      console.log("Apollo people search payload:", apolloPayload);

      // Search decision makers using Apollo API
      const response = await leadGenAPI.searchPeople(apolloPayload);

      console.log("Apollo API response:", response);

      // Extract enriched_data from Apollo API response
      let decisionMakersList = [];
      if (response.enriched_data && Array.isArray(response.enriched_data)) {
        // Apollo API returns enriched_data array with detailed information
        decisionMakersList = response.enriched_data;
      } else if (
        response.search_response?.people &&
        Array.isArray(response.search_response.people)
      ) {
        // Fallback to search_response.people if enriched_data is not available
        decisionMakersList = response.search_response.people;
      } else if (Array.isArray(response)) {
        decisionMakersList = response;
      } else if (response.data && Array.isArray(response.data)) {
        decisionMakersList = response.data;
      } else if (
        response.decision_makers &&
        Array.isArray(response.decision_makers)
      ) {
        decisionMakersList = response.decision_makers;
      } else if (response.people && Array.isArray(response.people)) {
        decisionMakersList = response.people;
      } else {
        // If response structure is different, try to extract array from response
        console.warn("Unexpected response structure:", response);
        decisionMakersList = [];
      }

      // Map enriched_data to match expected format
      const mappedDecisionMakers = decisionMakersList.map((dm) => {
        // Extract organization name and id from nested organization object
        const organizationName =
          dm.organization?.name || dm.organization_name || "";
        const organizationId = dm.organization?.id || dm.organization_id || "";

        // Build full name from first_name and last_name if name is not available
        const fullName =
          dm.name ||
          `${dm.first_name || ""} ${dm.last_name || ""}`.trim() ||
          "Unknown";

        // Extract location from formatted_address or build from city, state, country
        let location = dm.formatted_address || "";
        if (!location && (dm.city || dm.state || dm.country)) {
          const locationParts = [dm.city, dm.state, dm.country].filter(Boolean);
          location = locationParts.join(", ");
        }

        return {
          person_id: dm.id || dm.person_id,
          first_name: dm.first_name || "",
          last_name: dm.last_name || "",
          full_name: fullName,
          title: dm.title || dm.headline || "",
          email: dm.email || "",
          linkedin_url: dm.linkedin_url || "",
          photo_url: dm.photo_url || "",
          organization_id: organizationId,
          organization_name: organizationName,
          domain: dm.organization?.primary_domain || "",
          phone_number: dm.phone || dm.phone_number || "",
          location: location,
          city: dm.city || "",
          state: dm.state || "",
          country: dm.country || "",
          postal_code: dm.postal_code || "",
          headline: dm.headline || "",
          email_status: dm.email_status || "",
          seniority: dm.seniority || "",
          // Keep original data for reference
          ...dm,
        };
      });

      setDecisionMakers(mappedDecisionMakers);
      toast.success(`Found ${mappedDecisionMakers.length} decision makers`);
    } catch (error) {
      console.error("Error fetching decision makers:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to load decision makers";
      toast.error(errorMessage);
      setDecisionMakers([]);
    } finally {
      setIsLoadingDecisionMakers(false);
    }
  };

  const handleCompanyNameClick = (e, company) => {
    e.stopPropagation(); // Prevent card click
    if (company.website || company.website_url) {
      const url = company.website || company.website_url;
      window.open(url.startsWith("http") ? url : `https://${url}`, "_blank");
    }
  };

  const handleBackToBatches = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setSelectedBatch(null);
    setCompanies([]);
    setSelectedCompanies([]);
    setSelectedRoles([]);
    setDecisionMakers([]);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setSearchQuery(""); // Reset search when switching tabs
    handleBackToBatches(); // Reset view when switching tabs
  };

  // If viewing batch details (companies/employees), show that view
  if (selectedBatch) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          backgroundColor: "#FFFFFF",
          padding: { xs: "16px", sm: "24px" },
          overflow: "auto",
          position: "relative",
        }}
      >
        {/* Full Screen Loading Spinner */}
        {isLoadingCompanies && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 9999,
            }}
          >
            <CircularProgress size={60} />
          </Box>
        )}

        <Box
          sx={{
            maxWidth: "1400px",
            margin: "0 auto",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              marginBottom: "24px",
            }}
          >
            <Button
              type="button"
              startIcon={<ArrowBackIcon />}
              onClick={handleBackToBatches}
              sx={{
                textTransform: "none",
                color: "#3B82F6",
              }}
            >
              Back to Batches
            </Button>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {selectedBatch.search_name ||
                selectedBatch.keyword_name ||
                `Job ${selectedBatch.job_id?.substring(0, 8) || "Unknown"}`}
            </Typography>
          </Box>

          {/* Companies and Employees Grid */}
          <Grid container spacing={3}>
            {/* Left Panel: Companies */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "20px",
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Companies Found ({companies.length})
                </Typography>
              </Box>

              <Box
                sx={{ display: "flex", flexDirection: "column", gap: "16px" }}
              >
                {companies.length > 0 ? (
                  companies.map((company, index) => (
                    <Card
                      key={company.company_apollo_id || index}
                      onClick={() => handleCompanyToggle(company)}
                      sx={{
                        backgroundColor: "#FFFFFF",
                        borderRadius: "12px",
                        padding: "12px 16px",
                        cursor: "pointer",
                        border: selectedCompanies.includes(
                          company.place_id ||
                          company.company_apollo_id ||
                          company.id ||
                          `company_${companies.indexOf(company)}`
                        )
                          ? "2px solid #3B82F6"
                          : "1px solid #E5E7EB",
                        boxShadow: selectedCompanies.includes(
                          company.place_id ||
                          company.company_apollo_id ||
                          company.id ||
                          `company_${companies.indexOf(company)}`
                        )
                          ? "0 4px 6px rgba(0, 0, 0, 0.1)"
                          : "0 1px 3px rgba(0, 0, 0, 0.1)",
                        transition: "all 0.2s",
                        "&:hover": {
                          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                        },
                        position: "relative",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: "12px",
                        }}
                      >
                        <Checkbox
                          checked={selectedCompanies.includes(
                            company.place_id ||
                            company.company_apollo_id ||
                            company.id ||
                            `company_${companies.indexOf(company)}`
                          )}
                          onChange={() => handleCompanyToggle(company)}
                          onClick={(e) => e.stopPropagation()}
                          sx={{
                            padding: 0,
                            marginTop: "2px",
                          }}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          {/* Blue Icon in Top Right */}
                          <Box
                            sx={{
                              position: "absolute",
                              top: "12px",
                              right: "16px",
                            }}
                          >
                            <CorporateFareIcon
                              sx={{ color: "#3B82F6", fontSize: "20px" }}
                            />
                          </Box>

                          {/* Company Name - Large, Bold, Clickable */}
                          <Typography
                            variant="h6"
                            onClick={(e) => handleCompanyNameClick(e, company)}
                            sx={{
                              fontWeight: 700,
                              fontSize: "18px",
                              mb: 1.5,
                              paddingRight: "40px",
                              color: "#101828",
                              cursor:
                                company.website || company.website_url
                                  ? "pointer"
                                  : "default",
                              "&:hover": {
                                color:
                                  company.website || company.website_url
                                    ? "#3B82F6"
                                    : "#101828",
                                textDecoration:
                                  company.website || company.website_url
                                    ? "underline"
                                    : "none",
                              },
                              transition: "color 0.2s",
                            }}
                          >
                            {company.name}
                          </Typography>

                          {/* Business Type */}
                          {company.business_type && (
                            <Typography
                              variant="body2"
                              sx={{
                                color: "#6B7280",
                                mb: 1,
                                fontSize: "14px",
                                fontWeight: 500,
                              }}
                            >
                              Type: {company.business_type}
                            </Typography>
                          )}

                          {/* Rating */}
                          {company.rating && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                mb: 1,
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "#6B7280",
                                  fontSize: "14px",
                                  mr: 1,
                                }}
                              >
                                Rating: {company.rating}
                              </Typography>
                              {company.review_count && (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: "#9CA3AF",
                                    fontSize: "12px",
                                  }}
                                >
                                  ({company.review_count} reviews)
                                </Typography>
                              )}
                            </Box>
                          )}

                          {/* Phone Number */}
                          {company.phone_number && (
                            <Typography
                              variant="body2"
                              sx={{
                                color: "#6B7280",
                                mb: 1,
                                fontSize: "14px",
                              }}
                            >
                              Phone: {company.phone_number}
                            </Typography>
                          )}

                          {/* Full Address */}
                          {company.full_address && (
                            <Typography
                              variant="body2"
                              sx={{
                                color: "#6B7280",
                                mb: 0,
                                fontSize: "14px",
                                lineHeight: "20px",
                              }}
                            >
                              Address: {company.full_address}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Card>
                  ))
                ) : (
                  <Typography
                    sx={{
                      textAlign: "center",
                      padding: "20px",
                      color: "#6B7280",
                    }}
                  >
                    No companies found
                  </Typography>
                )}
              </Box>
            </Grid>

            {/* Right Panel: Decision Makers */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2.5,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Decision Makers
                </Typography>
              </Box>

              {/* Role Selection - Hide for LinkedIn */}
              {activeTab !== 1 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 500 }}>
                    Select Roles
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "8px",
                    }}
                  >
                    {availableRoles.map((role) => (
                      <Chip
                        key={role}
                        label={role}
                        onClick={() => handleRoleToggle(role)}
                        clickable
                        sx={{
                          backgroundColor: selectedRoles.includes(role)
                            ? "#3B82F6"
                            : "#FFFFFF",
                          color: selectedRoles.includes(role)
                            ? "#FFFFFF"
                            : "#101828",
                          border: "1px solid #D1D5DB",
                          height: "32px",
                          cursor: "pointer",
                          "&:hover": {
                            backgroundColor: selectedRoles.includes(role)
                              ? "#2563EB"
                              : "#F9FAFB",
                          },
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Number of Decision Makers Input - Hide for LinkedIn */}
              {activeTab !== 1 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 500 }}>
                    Number of Decision Makers (Max: 20)
                  </Typography>
                  <TextField
                    type="number"
                    fullWidth
                    value={noOfDecisionMakers}
                    onChange={(e) => {
                      const value = parseInt(e.target.value) || 1;
                      if (value >= 1 && value <= 20) {
                        setNoOfDecisionMakers(value);
                      } else if (value > 20) {
                        setNoOfDecisionMakers(20);
                        toast.error("Maximum 20 decision makers allowed");
                      } else if (value < 1) {
                        setNoOfDecisionMakers(1);
                      }
                    }}
                    inputProps={{
                      min: 1,
                      max: 20,
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        "& fieldset": {
                          borderColor: "#E5E7EB",
                        },
                        "&:hover fieldset": {
                          borderColor: "#D1D5DB",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#3B82F6",
                        },
                      },
                    }}
                  />
                </Box>
              )}

              {/* Find Decision Makers Button - Hide for LinkedIn */}
              {activeTab !== 1 && (
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleFindDecisionMakers}
                  disabled={
                    selectedCompanies.length === 0 ||
                    selectedRoles.length === 0 ||
                    !noOfDecisionMakers ||
                    noOfDecisionMakers < 1 ||
                    noOfDecisionMakers > 20
                  }
                  sx={{
                    mb: 3,
                    backgroundColor: "#3B82F6",
                    "&:hover": {
                      backgroundColor: "#2563EB",
                    },
                    textTransform: "none",
                    borderRadius: "8px",
                    padding: "12px",
                    fontWeight: 600,
                  }}
                >
                  Find Decision Makers
                </Button>
              )}

              {/* Decision Makers List */}
              {isLoadingDecisionMakers ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    padding: "40px",
                  }}
                >
                  <CircularProgress />
                </Box>
              ) : decisionMakers.length > 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                    maxHeight: "800px",
                    overflowY: "auto",
                  }}
                >
                  {decisionMakers.map((dm, index) => (
                    <Card
                      key={dm.person_id || index}
                      onClick={() => {
                        setSelectedDecisionMaker(dm);
                        setProfileModalOpen(true);
                      }}
                      sx={{
                        backgroundColor: "#FFFFFF",
                        borderRadius: "12px",
                        padding: "16px",
                        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        cursor: "pointer",
                        transition: "all 0.2s",
                        "&:hover": {
                          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                          transform: "translateY(-2px)",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                          flex: 1,
                        }}
                      >
                        {dm.photo_url ? (
                          <Box
                            component="img"
                            src={dm.photo_url}
                            alt={dm.full_name}
                            sx={{
                              width: "48px",
                              height: "48px",
                              borderRadius: "50%",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <Avatar
                            sx={{
                              width: "48px",
                              height: "48px",
                              bgcolor: "#D1D5DB",
                            }}
                          >
                            {dm.full_name?.charAt(0) || "U"}
                          </Avatar>
                        )}
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 600,
                              color: "#101828",
                              mb: 0.5,
                            }}
                          >
                            {dm.full_name}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#6B7280",
                              fontSize: "14px",
                            }}
                          >
                            {dm.title || "No title"}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: "flex", gap: "8px" }}>
                        {dm.email && (
                          <IconButton size="small" sx={{ color: "#6B7280" }}>
                            <EmailIcon fontSize="small" />
                          </IconButton>
                        )}
                        {dm.linkedin_url && (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(dm.linkedin_url, "_blank");
                            }}
                            sx={{ color: "#0A66C2" }}
                          >
                            <LinkedInIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </Card>
                  ))}
                </Box>
              ) : (
                <Typography
                  sx={{
                    textAlign: "center",
                    padding: "40px",
                    color: "#6B7280",
                  }}
                >
                  {selectedCompanies.length === 0
                    ? "Select companies and roles to find decision makers"
                    : selectedRoles.length === 0
                      ? "Select roles to find decision makers"
                      : "No decision makers found"}
                </Typography>
              )}
            </Grid>
          </Grid>
        </Box>

        {/* Employee Profile Modal */}
        <Dialog
          open={profileModalOpen}
          onClose={() => {
            setProfileModalOpen(false);
            setSelectedDecisionMaker(null);
          }}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "12px",
              overflow: "hidden",
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              pb: 1,
              fontWeight: 600,
            }}
          >
            Profile Details
            <IconButton
              onClick={() => {
                setProfileModalOpen(false);
                setSelectedDecisionMaker(null);
              }}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {selectedDecisionMaker && (
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 2 }}
              >
                {/* Profile Header */}
                <Box sx={{ display: "flex", gap: 2 }}>
                  {selectedDecisionMaker.photo_url ? (
                    <Box
                      component="img"
                      src={selectedDecisionMaker.photo_url}
                      alt={selectedDecisionMaker.full_name}
                      sx={{
                        width: "80px",
                        height: "80px",
                        borderRadius: "50%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <Avatar
                      sx={{
                        width: "80px",
                        height: "80px",
                        bgcolor: "#D1D5DB",
                      }}
                    >
                      {selectedDecisionMaker.full_name?.charAt(0) || "U"}
                    </Avatar>
                  )}
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {selectedDecisionMaker.full_name}
                    </Typography>
                    <Typography variant="body1" sx={{ color: "#3B82F6" }}>
                      {selectedDecisionMaker.title}
                    </Typography>
                    {selectedDecisionMaker.organization_name && (
                      <Typography variant="body2" sx={{ color: "#6B7280" }}>
                        {selectedDecisionMaker.organization_name}
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Contact Information */}
                <Box
                  sx={{
                    padding: "16px",
                    backgroundColor: "#F9FAFB",
                    borderRadius: "10px",
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                    Contact Information
                  </Typography>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                  >
                    {selectedDecisionMaker.email && (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <EmailIcon
                          sx={{ color: "#6B7280", fontSize: "20px" }}
                        />
                        <Typography variant="body2">
                          {selectedDecisionMaker.email}
                        </Typography>
                      </Box>
                    )}
                    {selectedDecisionMaker.phone_number && (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <PhoneIcon
                          sx={{ color: "#6B7280", fontSize: "20px" }}
                        />
                        <Typography variant="body2">
                          {selectedDecisionMaker.phone_number}
                        </Typography>
                      </Box>
                    )}
                    {selectedDecisionMaker.linkedin_url && (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <LinkedInIcon
                          sx={{ color: "#0A66C2", fontSize: "20px" }}
                        />
                        <Typography
                          component="a"
                          href={selectedDecisionMaker.linkedin_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ color: "#0A66C2", textDecoration: "none" }}
                        >
                          View LinkedIn Profile
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            )}
          </DialogContent>
        </Dialog>
      </Box>
    );
  }

  // Main Batches List View
  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        minHeight: "100%",
        backgroundColor: "#FFFFFF",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          overflowX: "hidden",
          minHeight: 0,
          padding: { xs: "16px", sm: "24px" },
          paddingBottom: {
            xs: "calc(60px + env(safe-area-inset-bottom, 0px))",
            sm: "calc(50px + env(safe-area-inset-bottom, 0px))",
            md: "24px"
          },
          WebkitOverflowScrolling: "touch",
          marginBottom: { xs: "0px", md: "0px" },
        }}
      >
        <Box
          sx={{
            maxWidth: "1400px",
            margin: "0 auto",
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 3 }}>
            Lead History
          </Typography>

          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              sx={{
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontWeight: 500,
                  fontSize: "16px",
                  minHeight: "48px",
                },
                "& .Mui-selected": {
                  color: "#3B82F6",
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: "#3B82F6",
                },
              }}
            >
              <Tab label="Google Maps" />
              <Tab label="LinkedIn" />
            </Tabs>
          </Box>

          {/* Google Maps Tab */}
          <TabPanel value={activeTab} index={0}>
            {/* Search Bar */}
            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                placeholder="Search batches by name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "#6B7280" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    backgroundColor: "#F9FAFB",
                    "& fieldset": {
                      borderColor: "#E5E7EB",
                    },
                    "&:hover fieldset": {
                      borderColor: "#D1D5DB",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#3B82F6",
                    },
                  },
                }}
              />
            </Box>

            {/* Batches List */}
            {isLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: "400px",
                }}
              >
                <CircularProgress />
              </Box>
            ) : filteredBatches.length > 0 ? (
              <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #E5E7EB", borderRadius: "12px" }}>
                <Table sx={{ minWidth: 650 }}>
                  <TableHead sx={{ backgroundColor: "#F9FAFB" }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, color: "#101828" }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#101828" }}>Job ID</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#101828" }}>Leads Found</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#101828", textAlign: "right" }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredBatches.map((batch, index) => (
                      <TableRow
                        key={index}
                        hover
                        onClick={() => handleBatchClick(batch)}
                        sx={{ cursor: "pointer", "&:last-child td, &:last-child th": { border: 0 } }}
                      >
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <BusinessIcon sx={{ color: "#3B82F6", mr: 1.5, fontSize: "20px" }} />
                            <Typography variant="body2" sx={{ fontWeight: 600, color: "#101828" }}>
                              {batch.search_name || "Unnamed Search"}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: "#6B7280" }}>
                            {batch.job_id?.substring(0, 12) || "N/A"}...
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={`${batch.job_id_count || 0} companies`}
                            size="small"
                            sx={{
                              backgroundColor: "#DBEAFE",
                              color: "#1E40AF",
                              fontWeight: 600,
                              fontSize: "0.75rem"
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ textAlign: "right" }}>
                          <IconButton size="small" sx={{ color: "#3B82F6" }}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Card
                sx={{
                  padding: "40px",
                  textAlign: "center",
                  backgroundColor: "#F9FAFB",
                }}
              >
                <Typography variant="body1" sx={{ color: "#6B7280" }}>
                  {searchQuery
                    ? "No batches found matching your search"
                    : "No batches found"}
                </Typography>
              </Card>
            )}
          </TabPanel>

          {/* LinkedIn Tab */}
          <TabPanel value={activeTab} index={1}>
            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                placeholder="Search jobs by search name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "#6B7280" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    backgroundColor: "#F9FAFB",
                    "& fieldset": {
                      borderColor: "#E5E7EB",
                    },
                    "&:hover fieldset": {
                      borderColor: "#D1D5DB",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#3B82F6",
                    },
                  },
                }}
              />
            </Box>

            {isLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: "400px",
                }}
              >
                <CircularProgress />
              </Box>
            ) : filteredLinkedinJobs.length > 0 ? (
              <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #E5E7EB", borderRadius: "12px" }}>
                <Table sx={{ minWidth: 650 }}>
                  <TableHead sx={{ backgroundColor: "#F9FAFB" }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, color: "#101828" }}>Search Name</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#101828" }}>Job ID</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#101828" }}>Leads Found</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#101828", textAlign: "right" }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredLinkedinJobs.map((job, index) => (
                      <TableRow
                        key={index}
                        hover
                        onClick={() => handleBatchClick(job)}
                        sx={{ cursor: "pointer", "&:last-child td, &:last-child th": { border: 0 } }}
                      >
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <LinkedInIcon sx={{ color: "#0A66C2", mr: 1.5, fontSize: "20px" }} />
                            <Typography variant="body2" sx={{ fontWeight: 600, color: "#101828" }}>
                              {job.search_name || `Job ${job.job_id?.substring(0, 8) || "Unknown"}`}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: "#6B7280" }}>
                            {job.job_id?.substring(0, 12) || "N/A"}...
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={`${job.row_count || 0} companies`}
                            size="small"
                            sx={{
                              backgroundColor: "#DBEAFE",
                              color: "#1E40AF",
                              fontWeight: 600,
                              fontSize: "0.75rem"
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ textAlign: "right" }}>
                          <IconButton size="small" sx={{ color: "#3B82F6" }}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Card
                sx={{
                  padding: "40px",
                  textAlign: "center",
                  backgroundColor: "#F9FAFB",
                }}
              >
                <Typography variant="body1" sx={{ color: "#6B7280" }}>
                  {searchQuery
                    ? "No jobs found matching your search"
                    : "No LinkedIn jobs found"}
                </Typography>
              </Card>
            )}
          </TabPanel>
        </Box>
      </Box>
    </Box>
  );
};

export default LeadHistory;
